import React, { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { HeartHandshake, Feather } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

/* Dropout Prevention — gentle care check-in shown only when the coach detects risk. */
export default function CareCard({ onApplied }) {
  const { tx, t } = useLanguage();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    api.get("/dropout/status").then((r) => setStatus(r.data)).catch(() => {});
  }, []);

  const respond = useCallback(async (action) => {
    setBusy(true);
    try {
      await api.post("/dropout/respond", { action });
      if (action === "lighter_plan") {
        toast.success(t("careCard.todaySPlanIsNow"));
        onApplied?.();
      }
      setHidden(true);
    } catch {
      toast.error(t("careCard.couldnTUpdateYourPlan"));
    } finally {
      setBusy(false);
    }
  }, [onApplied, t]);

  if (hidden || !status?.suggestion || status.responded_today) return null;
  const { assessment, suggestion } = status;

  return (
    <section
      className="rounded-2xl border border-gold-500/40 bg-gradient-to-br from-sand-100 to-gold-500/10 p-5"
      data-testid="care-card"
    >
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 shrink-0 rounded-full bg-forest-700 grid place-items-center">
          <HeartHandshake className="h-5 w-5 text-gold-500" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-semibold text-forest-700">{t("careCard.aGentleCheckInFrom")}</div>
          <p className="mt-1 text-sm text-foreground/80" data-testid="care-message">{tx(suggestion.message)}</p>
          {suggestion.hadith && (
            <blockquote className="mt-3 border-l-2 border-gold-500 pl-3 text-sm italic text-foreground/70" data-testid="care-hadith">
              “{tx(suggestion.hadith.text)}”
              <span className="block not-italic text-[11px] mt-1 text-muted-foreground">{suggestion.hadith.source}</span>
            </blockquote>
          )}
          {assessment.factors.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {assessment.factors.map((f) => (
                <span key={f} className="text-[11px] px-2 py-0.5 rounded-full bg-sand-200 text-foreground/70">
                  {f}
                </span>
              ))}
            </div>
          )}
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              onClick={() => respond("lighter_plan")}
              disabled={busy}
              className="btn-primary text-xs disabled:opacity-50"
              data-testid="care-accept-lighter"
            >
              <Feather className="h-3.5 w-3.5" /> {t("careCard.takeTodaySLighterPlan")}
            </button>
            <button
              onClick={() => respond("keep_plan")}
              disabled={busy}
              className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 disabled:opacity-50"
              data-testid="care-keep-plan"
            >
              {t("careCard.iMOkayKeepMy")}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
